<!-- eslint-disable -->
<template>
    <div class="minimal-auth-wrapper">
        <div class="auth-container">
            <!-- Logo Section -->
            <div class="logo-section" v-if="app.logo">
                <b-img :src="app.logo" alt="Logo" class="brand-logo" />
            </div>

            <!-- Main Card -->
            <div class="login-card">
                <!-- Loading State - Verificando -->
                <div v-if="isVerifying" class="text-center">
                    <div class="login-header">
                        <h1 class="login-title">Verificando enlace...</h1>
                    </div>

                    <div class="spinner-container">
                        <b-spinner
                            style="width: 3rem; height: 3rem"
                            label="Verificando..."
                            variant="primary"
                        />
                    </div>

                    <p class="text-muted mt-3">
                        Por favor espera mientras verificamos tu enlace de
                        acceso.
                    </p>
                </div>

                <!-- Confirmation State - Mostrar datos del usuario y botón confirmar -->
                <div v-else-if="showConfirmation" class="text-center">
                    <div class="login-header">
                        <h1 class="login-title">¡Hola, {{ userName }}!</h1>
                        <p class="login-subtitle">
                            Estás a punto de iniciar sesión con el correo:
                        </p>
                    </div>

                    <div class="email-display">
                        <feather-icon icon="MailIcon" size="20" />
                        <strong>{{ userEmail }}</strong>
                    </div>

                    <b-button
                        variant="primary"
                        block
                        class="submit-btn"
                        :disabled="isConfirming"
                        @click="handleConfirm"
                    >
                        <b-spinner v-if="isConfirming" small class="mr-2" />
                        {{
                            isConfirming ? "Confirmando..." : "Confirmar acceso"
                        }}
                    </b-button>

                    <div class="info-text mt-3">
                        <feather-icon icon="InfoIcon" size="16" />
                        <span>¿No fuiste tú? Ignora este enlace.</span>
                    </div>
                </div>

                <!-- Success State - Redirigiendo -->
                <div v-else-if="verificationSuccess" class="text-center">
                    <div class="login-header">
                        <h1 class="login-title success-title">
                            ¡Acceso exitoso!
                        </h1>
                    </div>

                    <div class="success-alert">
                        <div class="alert-icon">
                            <feather-icon icon="CheckCircleIcon" size="32" />
                        </div>
                        <div class="alert-content">
                            <strong>Bienvenido de vuelta</strong>
                            <p class="mb-0">
                                Serás redirigido al portal en breve...
                            </p>
                        </div>
                    </div>

                    <div class="redirect-indicator">
                        <b-spinner small class="mr-2" />
                        <span>Redirigiendo...</span>
                    </div>
                </div>

                <!-- Error State -->
                <div v-else-if="verificationError" class="text-center">
                    <div class="login-header">
                        <h1 class="login-title error-title">
                            Enlace inválido o expirado
                        </h1>
                    </div>

                    <div class="error-alert">
                        <div class="alert-icon">
                            <feather-icon icon="AlertCircleIcon" size="32" />
                        </div>
                        <div class="alert-content">
                            {{ errorMessage }}
                        </div>
                    </div>

                    <b-button
                        variant="primary"
                        block
                        class="submit-btn mb-3"
                        @click="goToLogin"
                    >
                        <feather-icon icon="ArrowLeftIcon" class="mr-1" />
                        Volver al login
                    </b-button>

                    <b-button
                        v-if="$route.query.email"
                        variant="outline-secondary"
                        block
                        class="back-btn"
                        :disabled="isRequestingNew"
                        @click="requestNewLink"
                    >
                        <feather-icon icon="MailIcon" class="mr-1" />
                        {{
                            isRequestingNew
                                ? "Enviando..."
                                : "Solicitar nuevo enlace"
                        }}
                    </b-button>
                </div>
            </div>

            <!-- Payment Methods -->
            <div class="payment-methods">
                <p class="payment-label">Métodos de pago</p>
                <div class="payment-images">
                    <b-img :src="brands.visa" alt="Visa" class="brand-img" />
                    <b-img
                        :src="brands.mastercard"
                        alt="Mastercard"
                        class="brand-img"
                    />
                    <b-img
                        :src="brands.visaSecure"
                        alt="Visa Secure"
                        class="brand-img"
                    />
                    <b-img
                        :src="brands.mcIdCheck"
                        alt="Mastercard ID Check"
                        class="brand-img"
                    />
                    <b-img
                        :src="imgPayPal"
                        alt="PayPal"
                        class="payment-img-paypal"
                    />
                </div>
            </div>

            <!-- Enlaces legales -->
            <div class="legal-links">
                <b-link :to="{ name: 'legal-services' }">Servicios</b-link>
                <b-link :to="{ name: 'legal-terms' }">Términos</b-link>
                <b-link :to="{ name: 'legal-privacy' }">Privacidad</b-link>
                <b-link :to="{ name: 'legal-security' }">Seguridad</b-link>
                <b-link :to="{ name: 'legal-refunds' }">Devoluciones</b-link>
                <b-link :to="{ name: 'legal-delivery' }">Entrega</b-link>
                <b-link :to="{ name: 'legal-contact' }">Contacto</b-link>
            </div>

            <!-- Datos del comercio -->
            <div class="business-info">
                <div>
                    {{ business.name }} · {{ business.address }} ·
                    {{ business.country }}
                </div>
                <div>
                    <a :href="`tel:${business.phone}`">{{ business.phone }}</a>
                    ·
                    <a :href="`mailto:${business.email}`">{{
                        business.email
                    }}</a>
                </div>
            </div>

            <!-- Footer -->
            <div class="page-footer">
                <feather-icon icon="LockIcon" size="14" />
                <span>Conexión segura</span>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable */
import useJwt from "@/auth/jwt/useJwt";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
    BAlert,
    BButton,
    BCard,
    BCardText,
    BCardTitle,
    BCol,
    BImg,
    BLink,
    BRow,
    BSpinner,
} from "bootstrap-vue";
import business from "@/business";

export default {
    components: {
        BRow,
        BCol,
        BButton,
        BCard,
        BCardTitle,
        BLink,
        BCardText,
        BSpinner,
        BAlert,
        ToastificationContent,
        BImg,
    },
    data() {
        return {
            isVerifying: true,
            showConfirmation: false,
            isConfirming: false,
            verificationSuccess: false,
            verificationError: false,
            errorMessage: "",
            isRequestingNew: false,
            userName: "",
            userEmail: "",
            magicLinkToken: "",
            app: {
                banner: "",
                name: "",
                logo: "",
            },
            business,
            imgPayPal: require("@/assets/images/logo/paypal.png"),
            brands: {
                visa: require("@/assets/images/brands/visa.jpg"),
                mastercard: require("@/assets/images/brands/mastercard.jpg"),
                visaSecure: require("@/assets/images/brands/visa-secure.png"),
                mcIdCheck: require("@/assets/images/brands/mastercard-idcheck.png"),
            },
        };
    },
    created() {
        this.$http
            .get("/get-env")
            .then((response) => {
                this.app.banner = response.data.data.app_banner;
                this.app.name = response.data.data.app_name;
                this.app.logo = response.data.data.app_logo;
            })
            .catch((error) => {
                console.log(error);
            });
    },
    mounted() {
        this.verifyMagicLink();
    },
    methods: {
        async verifyMagicLink() {
            const { token } = this.$route.params;

            if (!token) {
                this.handleVerificationError("Token no encontrado");

                return;
            }

            // Guardar el token para usarlo en el paso de confirmación
            this.magicLinkToken = token;

            try {
                // Paso 1: Validar el token (GET - solo valida, no autentica)
                const response = await useJwt.verifyMagicLink(token);

                if (response.data.valid) {
                    // Token válido - mostrar pantalla de confirmación
                    this.userName = response.data.userName || "Usuario";
                    this.userEmail = response.data.email || "";
                    this.isVerifying = false;
                    this.showConfirmation = true;
                } else {
                    // Token inválido
                    this.handleVerificationError(
                        response.data.error || "Enlace inválido o expirado"
                    );
                }
            } catch (error) {
                console.error("Magic link validation error:", error);
                this.handleVerificationError(
                    error.response?.data?.error ||
                        error.response?.data?.message ||
                        "El enlace ya fue utilizado o ha expirado"
                );
            }
        },

        async handleConfirm() {
            this.isConfirming = true;

            try {
                // Limpiar cualquier token previo
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("userData");

                // Paso 2: Confirmar y obtener token de acceso (POST)
                const response = await useJwt.confirmMagicLink(
                    this.magicLinkToken
                );

                // Verificar que la respuesta tenga los datos necesarios
                if (
                    !response.data ||
                    !response.data.accessToken ||
                    !response.data.userData
                ) {
                    throw new Error("Respuesta del servidor incompleta");
                }

                // Configurar tokens y datos del usuario
                useJwt.setToken(response.data.accessToken);

                if (response.data.refreshToken) {
                    useJwt.setRefreshToken(response.data.refreshToken);
                }

                if (response.data.userData) {
                    localStorage.setItem(
                        "userData",
                        JSON.stringify(response.data.userData)
                    );
                }

                if (response.data.paraIsp) {
                    localStorage.setItem(
                        "paraIsp",
                        JSON.stringify(response.data.paraIsp)
                    );
                }

                // Actualizar habilidades
                if (response.data.userData && response.data.userData.ability) {
                    this.$ability.update(response.data.userData.ability);
                }

                // Mostrar éxito
                this.showConfirmation = false;
                this.verificationSuccess = true;

                // Mostrar toast de éxito
                this.showToast(
                    response.data.message || "Acceso exitoso",
                    "success",
                    "CheckCircleIcon"
                );

                // Redirigir después de 2 segundos
                setTimeout(() => {
                    this.$router.push("/");
                }, 2000);
            } catch (error) {
                console.error("Magic link confirmation error:", error);

                // Limpiar localStorage en caso de error
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("userData");

                this.showConfirmation = false;
                this.handleVerificationError(
                    error.response?.data?.error ||
                        error.response?.data?.message ||
                        "Error al iniciar sesión. El enlace puede haber expirado."
                );
            } finally {
                this.isConfirming = false;
            }
        },

        handleVerificationError(message) {
            this.isVerifying = false;
            this.showConfirmation = false;
            this.verificationError = true;
            this.errorMessage = message;
        },

        goToLogin() {
            this.$router.push({ name: "auth-login" });
        },

        async requestNewLink() {
            const { email } = this.$route.query;

            if (!email) {
                this.$swal({
                    title: "Error",
                    text: "No se pudo determinar el email. Por favor vuelva al login.",
                    icon: "error",
                });

                return;
            }

            this.isRequestingNew = true;

            try {
                await useJwt.sendMagicLink(email);

                this.showToast(
                    "Nuevo enlace enviado a tu email",
                    "success",
                    "MailIcon"
                );

                // Redirigir a página de confirmación
                this.$router.push({
                    name: "magic-link-sent",
                    params: { email },
                });
            } catch (error) {
                this.$swal({
                    title: "Error",
                    text:
                        error.response?.data?.message ||
                        "Error enviando el enlace",
                    icon: "error",
                });
            } finally {
                this.isRequestingNew = false;
            }
        },

        showToast(message, variant, icon) {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title: message,
                    variant,
                    icon,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/pages/page-auth.scss";
@import "@/assets/scss/auth-minimal";

.login-title.success-title {
    color: var(--tm-success);
}

.login-title.error-title {
    color: var(--tm-danger);
}
</style>
